import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { useState } from "react";

import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

const DataTableInvoicingData = <TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const tableInvoicingData = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 2,
      },
    },
  });

  const pageIndex = tableInvoicingData.getState().pagination.pageIndex;
  const pageCount = tableInvoicingData.getPageCount();

  return (
    <div className="flex flex-col max-h-[550px]">
      <div className="w-full">
        <div className="h-fit overflow-y-auto w-full shadow-md">
          <Table>
            <TableHeader>
              {tableInvoicingData.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={
                          header.id === "actions"
                            ? "w-[50px] max-w-[50px] truncate"
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {tableInvoicingData.getRowModel().rows?.length ? (
                tableInvoicingData.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="truncate">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No hay resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between py-4 w-full">
          <div>
            <span className="text-sm font-[Inter]">
              Página {pageIndex + 1} de {pageCount}
            </span>
          </div>
          <div className="space-x-2">
            <Button
              variant={"outline"}
              className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
              size="sm"
              onClick={() => tableInvoicingData.previousPage()}
              disabled={!tableInvoicingData.getCanPreviousPage()}
            >
              Anterior
            </Button>
            <Button
              variant={"outline"}
              className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
              size="sm"
              onClick={() => tableInvoicingData.nextPage()}
              disabled={!tableInvoicingData.getCanNextPage()}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTableInvoicingData;
